import { ErrorListResponse, instanceOfErrorListResponse, Service } from '@genial/services';
import { FixedFeeAgreementSuccessResponse } from '@genial/services/src';
import { useRecoilValue } from 'recoil';

import { useToastCreator } from '../../hooks/useToastCreator';
import { advisorIdToApisSelector } from '../master/selector';

import {
  cancelPendingSuccessMessage,
  cancelActiveSuccessMessage,
  defaultErrorMessage,
  defaultNotificationResendMessage,
  fixedFeeScreenName,
} from './constants';
import { FixedFeeReportGenerationProps } from './types';

export const useFixedFeeActions = () => {
  const selectedAdvisor = useRecoilValue(advisorIdToApisSelector);
  const { showToast, showToastFromError } = useToastCreator('fixed-fee-actions');

  const postCancelTerm = async (
    feeBasedContractId: number,
    activeWithMinimumStayEnded: boolean
  ) => {
    return Service.FixedFee.V1.postAgreementByFeeBasedContractCancel({
      screenName: fixedFeeScreenName,
      feeBasedContractId,
    })
      .then(({ data }) => {
        if (data.success) {
          if (activeWithMinimumStayEnded) {
            showToast(...cancelActiveSuccessMessage);
            return;
          }
          showToast(...cancelPendingSuccessMessage);
          return;
        }
        if (data.error.errorTitle && data.error.errorDisplay) {
          showToast('error', data.error.errorTitle, data.error.errorDisplay);
          return;
        }
        throw new Error(data.error.errorDisplay || '');
      })
      .catch((error) => {
        if (instanceOfErrorListResponse(error as object)) {
          (error as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }
        showToast(...defaultErrorMessage);
      });
  };

  const postCreateTerm = async (
    accountNumber: number,
    feePercentage: number,
    notificationChannels: string[],
    excludingEquities: boolean
  ): Promise<FixedFeeAgreementSuccessResponse> => {
    try {
      return await Service.FixedFee.V1.postAgreement({
        screenName: fixedFeeScreenName,
        createFixedFeeAgreementRequest: {
          accountNumber,
          feePercentage,
          excludedProducts: excludingEquities ? ['equity'] : undefined,
          notificationChannels,
          advisorId: selectedAdvisor,
        },
      });
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        throw error;
      }

      showToast(...defaultErrorMessage);
      throw error;
    }
  };

  const postResendNotification = async (feeBasedContractId: number) => {
    try {
      const { data } = await Service.FixedFee.V1.postAgreementByFeeBasedContractResend({
        screenName: fixedFeeScreenName,
        feeBasedContractId,
      });
      if (data.success) {
        showToast(...defaultNotificationResendMessage);
        return;
      }
      if (data.error.errorTitle && data.error.errorDisplay) {
        showToast('error', data.error.errorTitle, data.error.errorDisplay);
        return;
      }
      throw new Error(data.error.errorDisplay || '');
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        throw error;
      }

      showToast(...defaultErrorMessage);
      throw error;
    }
  };

  const postGenerateReport = async ({
    advisorId,
    endDate,
    startDate,
    partnerId,
  }: FixedFeeReportGenerationProps) => {
    try {
      const { data } = await Service.FixedFee.V1.getAgreementFeeBasedContractReportFileUrlFilter({
        screenName: fixedFeeScreenName,
        advisorId,
        endDate,
        startDate,
        partnerId,
      });
      if (window && data.url) {
        window.open(data.url, '_blank');
        return;
      }
      throw new Error('Não foi possível gerar o relatório');
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        throw error;
      }

      showToast(...defaultErrorMessage);
      throw error;
    }
  };

  return { postCancelTerm, postCreateTerm, postResendNotification, postGenerateReport };
};
